"use client";

import { c as _c } from "react/compiler-runtime";
import { Menu, X } from "lucide-react";
import Image from "next/image";
import Link from "next/link";
import { useEffect, useRef, useState } from "react";
import { TrackedAnchorButton } from "~/components/ui/TrackedAnchorButton";
import { css } from "~/generated/styled-system/css";
import { cq, flex, grid } from "~/generated/styled-system/patterns";
import { navLink } from "~/generated/styled-system/recipes";
import { useScrolledPast } from "~/hooks/useScrolledPast";
import { GA_EVENT_NAMES } from "~/lib/GoogleAds";
import { ZOOM_SCHEDULER_URL } from "~/lib/scheduling";
import HeaderLogo from "../../../../public/header-logo.webp";
const NAV_LINKS = [{
  href: "/about",
  label: "About"
}, {
  href: "/#specialties",
  label: "Specialties"
}, {
  href: "/#testimonials",
  label: "Testimonials"
}, {
  href: "/#contact",
  label: "Contact"
}, {
  href: "/#faq",
  label: "FAQ"
}];
export default function Navbar() {
  const $ = _c(32);
  const scrolled = useScrolledPast(60);
  const [open, setOpen] = useState(false);
  const burgerRef = useRef(null);
  let t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = () => {
      const burger = burgerRef.current;
      if (!burger) {
        return;
      }
      const observer = new ResizeObserver(t2 => {
        const [entry] = t2;
        if (entry?.contentRect.width === 0) {
          setOpen(false);
        }
      });
      observer.observe(burger);
      return () => observer.disconnect();
    };
    t1 = [];
    $[0] = t0;
    $[1] = t1;
  } else {
    t0 = $[0];
    t1 = $[1];
  }
  useEffect(t0, t1);
  const t2 = scrolled || undefined;
  let t3;
  let t4;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = cq({
      position: "sticky",
      top: 0,
      zIndex: 50,
      bg: "colorPalette.surface",
      borderBlockEnd: "1px solid",
      borderBlockEndColor: "colorPalette.divider",
      transitionProperty: "box-shadow",
      transitionDuration: "normal",
      "&[data-scrolled]": {
        boxShadow: "sm"
      }
    });
    t4 = flex({
      alignItems: "center",
      justifyContent: "space-between",
      gap: "4",
      height: "20",
      paddingInline: "4",
      marginInline: "auto",
      "@/4xl": {
        height: "28",
        paddingInline: "6",
        inlineSize: "4xl"
      },
      "@/6xl": {
        inlineSize: "6xl"
      },
      "@/8xl": {
        inlineSize: "8xl"
      }
    });
    $[2] = t3;
    $[3] = t4;
  } else {
    t3 = $[2];
    t4 = $[3];
  }
  let t5;
  if ($[4] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = css({
      display: "inline-flex",
      alignItems: "center",
      lineHeight: 1,
      textDecoration: "none"
    });
    $[4] = t5;
  } else {
    t5 = $[4];
  }
  let t6;
  let t7;
  let t8;
  if ($[5] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = <Link href="/" aria-label={"Balance Seekers \u2014 Linda Ryan, LCSW"} className={t5}><Image src={HeaderLogo} alt="Balance Seekers" placeholder="blur" sizes="(min-width: 896px) 134px, 90px" className={css({
        height: "16",
        width: "auto",
        "@/4xl": {
          height: "24"
        }
      })} /></Link>;
    t7 = <ul className={flex({
      display: "none",
      "@/4xl": {
        display: "flex",
        gap: "8",
        alignItems: "center",
        listStyle: "none",
        padding: 0,
        margin: 0
      }
    })}>{NAV_LINKS.map(_temp)}</ul>;
    t8 = css({
      display: "none",
      "@/4xl": {
        display: "block"
      }
    });
    $[5] = t6;
    $[6] = t7;
    $[7] = t8;
  } else {
    t6 = $[5];
    t7 = $[6];
    t8 = $[7];
  }
  let t9;
  if ($[8] === Symbol.for("react.memo_cache_sentinel")) {
    t9 = <div className={t8}><TrackedAnchorButton href={ZOOM_SCHEDULER_URL} target="_blank" rel="noopener noreferrer" ctaName={GA_EVENT_NAMES.SCHEDULE_ZOOM_MEETING} eventCategory="navigation" variant="solid" tone="primary" shape="pill">Book a free call<span className={css({
          srOnly: true
        })}>(opens in a new tab)</span></TrackedAnchorButton></div>;
    $[8] = t9;
  } else {
    t9 = $[8];
  }
  const t10 = open ? "Close menu" : "Open menu";
  let t11;
  let t12;
  if ($[9] === Symbol.for("react.memo_cache_sentinel")) {
    t11 = () => setOpen(_temp2);
    t12 = css({
      display: "inline-flex",
      alignItems: "center",
      justifyContent: "center",
      width: "11",
      height: "11",
      borderRadius: "md",
      border: "none",
      bg: "transparent",
      color: "colorPalette.ink",
      cursor: "pointer",
      "@/4xl": {
        display: "none"
      },
      _hover: {
        bg: "colorPalette.surfaceAlt"
      },
      focusVisibleRing: "outside"
    });
    $[9] = t11;
    $[10] = t12;
  } else {
    t11 = $[9];
    t12 = $[10];
  }
  let t13;
  if ($[11] !== open) {
    t13 = open ? <X size={22} aria-hidden="true" /> : <Menu size={22} aria-hidden="true" />;
    $[11] = open;
    $[12] = t13;
  } else {
    t13 = $[12];
  }
  let t14;
  if ($[13] !== open || $[14] !== t10 || $[15] !== t13) {
    t14 = <div className={t4}>{t6}{t7}{t9}<button ref={burgerRef} type="button" aria-label={t10} aria-expanded={open} aria-controls="mobile-nav-drawer" onClick={t11} className={t12}>{t13}</button></div>;
    $[13] = open;
    $[14] = t10;
    $[15] = t13;
    $[16] = t14;
  } else {
    t14 = $[16];
  }
  const t15 = !open;
  let t16;
  if ($[17] === Symbol.for("react.memo_cache_sentinel")) {
    t16 = css({
      position: "absolute",
      insetInline: 0,
      top: "20",
      bg: "colorPalette.surface",
      borderBlockEnd: "1px solid",
      borderBlockEndColor: "colorPalette.divider",
      paddingInline: "5",
      paddingBlockStart: "3",
      paddingBlockEnd: "5",
      opacity: 0,
      transform: "translateY(-8px)",
      pointerEvents: "none",
      visibility: "hidden",
      transitionProperty: "transform, opacity, visibility",
      transitionDuration: "240ms",
      boxShadow: "md",
      "&[data-open]": {
        opacity: 1,
        transform: "translateY(0)",
        pointerEvents: "auto",
        visibility: "visible"
      },
      "@media (prefers-reduced-motion: reduce)": {
        transform: "none",
        transitionDuration: "0s"
      },
      "@/4xl": {
        display: "none"
      }
    });
    $[17] = t16;
  } else {
    t16 = $[17];
  }
  let t17;
  if ($[18] !== open) {
    t17 = open ? {
      "data-open": ""
    } : {};
    $[18] = open;
    $[19] = t17;
  } else {
    t17 = $[19];
  }
  let t18;
  let t19;
  if ($[20] === Symbol.for("react.memo_cache_sentinel")) {
    t18 = <ul className={grid({
      gap: 0,
      listStyle: "none",
      padding: 0,
      margin: 0
    })}>{NAV_LINKS.map(link_0 => <li key={link_0.href}><a href={link_0.href} onClick={() => setOpen(false)} className={navLink({
          variant: "drawer"
        })}>{link_0.label}</a></li>)}</ul>;
    t19 = css({
      marginBlockStart: "4"
    });
    $[20] = t18;
    $[21] = t19;
  } else {
    t18 = $[20];
    t19 = $[21];
  }
  let t20;
  let t21;
  if ($[22] === Symbol.for("react.memo_cache_sentinel")) {
    t20 = () => setOpen(false);
    t21 = css({
      width: "full"
    });
    $[22] = t20;
    $[23] = t21;
  } else {
    t20 = $[22];
    t21 = $[23];
  }
  let t22;
  if ($[24] === Symbol.for("react.memo_cache_sentinel")) {
    t22 = <div className={t19}><TrackedAnchorButton href={ZOOM_SCHEDULER_URL} target="_blank" rel="noopener noreferrer" ctaName={GA_EVENT_NAMES.SCHEDULE_ZOOM_MEETING} eventCategory="navigation" variant="solid" tone="primary" shape="pill" onClick={t20} className={t21}>Book a free call<span className={css({
          srOnly: true
        })}>(opens in a new tab)</span></TrackedAnchorButton></div>;
    $[24] = t22;
  } else {
    t22 = $[24];
  }
  let t23;
  if ($[25] !== t15 || $[26] !== t17) {
    t23 = <div id="mobile-nav-drawer" aria-hidden={t15} className={t16} {...t17}>{t18}{t22}</div>;
    $[25] = t15;
    $[26] = t17;
    $[27] = t23;
  } else {
    t23 = $[27];
  }
  let t24;
  if ($[28] !== t14 || $[29] !== t2 || $[30] !== t23) {
    t24 = <nav aria-label="Main" data-scrolled={t2} className={t3}>{t14}{t23}</nav>;
    $[28] = t14;
    $[29] = t2;
    $[30] = t23;
    $[31] = t24;
  } else {
    t24 = $[31];
  }
  return t24;
}
function _temp2(v) {
  return !v;
}
function _temp(link) {
  return <li key={link.href}><a href={link.href} className={navLink({
      variant: "bar"
    })}>{link.label}</a></li>;
}